/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import { LocalDate } from '@js-joda/core';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { CardModel } from 'src/types/card.model';
import DebtService, { PayDownData } from '../../services/debt.service';
import FormatService from '../../services/format.service';

// components
import BwSlider from '../bw-slider/BwSlider';
import GoalDrawer from './GoalDrawer';
import { HELP_DOMAIN } from 'src/utils/constants';
import InfoCallout from '../callout/InfoCallout';
import LiteralsService from '../../services/literals/LiteralsService';
import Output from '../atomic/molecules/output/Output';
import SectionHeader from '../section-header/SectionHeader';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and styles
import { ReactComponent as PayDownDebtIcon } from 'src/bw/goal/pay-down-debt.svg';
import styles from './PayDownDebtDrawer.module.scss';

interface DrawerState {
    startDate: LocalDate;
    cardId: number;
    monthly: number;
    payDownData: PayDownData;
    snapshot: PayDownData;
}

export default class PayDownDebtDrawer extends Component<any, DrawerState> {
    private readonly handleUpdateOnce: () => void = _.once(async () => await this.handleUpdate());

    public override state: DrawerState = {} as DrawerState;

    public static getDerivedStateFromProps(props: any, prevState: DrawerState): DrawerState {
        const state: DrawerState = prevState;
        const propsDate: LocalDate = _.get(props, 'drawerProps.card.startDate');
        const monthly: number = _.get(props, 'drawerProps.card.attributes.monthly') || 0;
        const payDownData: PayDownData = _.get(props, 'drawerProps.payDownData');

        // eslint-disable-next-line
        if (prevState.startDate == undefined && propsDate) {
            state.startDate = propsDate;
        }

        // eslint-disable-next-line
        if (prevState.monthly == undefined) {
            state.monthly = monthly;
        }

        // eslint-disable-next-line
        if (prevState.payDownData == undefined && payDownData) {
            state.payDownData = payDownData;
            state.snapshot = payDownData;
        }

        return state;
    }

    public override render(): ReactComponentElement<any> {
        let payoffDate: LocalDate;
        let snapshotDate: LocalDate;
        let month: string;
        let payoffDateString: string;
        if (_.get(this, 'state.payDownData.estimatedPayoffDate') && _.get(this, 'state.snapshot.estimatedPayoffDate')) {
            payoffDate = LocalDate.parse(this.state.payDownData.estimatedPayoffDate);
            snapshotDate = LocalDate.parse(this.state.snapshot.estimatedPayoffDate);
            month = payoffDate.month().toString().toLowerCase();
            month = `${month.substr(0, 3)}.`;
            payoffDateString = `${month} ${payoffDate.year()}`;
        }

        const drawerProps: any = this.props.drawerProps;
        const card: CardModel = drawerProps.card;
        const debts: Array<{
            amount: number;
            id: number;
            interest: number;
            name: string;
            payoffDate: string;
        }> = _.get(this, 'state.payDownData.debts') || [];

        // This drawer has a different look (and text) the first time it is opened.
        const creatingCard: boolean = !_.has(card.attributes, 'monthly');

        const infoCallout: JSX.Element = debts.length ? (
            <InfoCallout
                text="We assume that every month you will pay a minimum of 1% of the current principal plus accrued interest.
                By increasing your monthly payment to pay more than this minimum, you can pay off debt sooner and save
                on interest."
                noMargin
            />
        ) : (
            <InfoCallout
                text="You have no high interest debt during this period. Change your start date to when you have high interest
                debt."
                noMargin
            />
        );

        const totalDebt: number = debts.length ? _.sumBy(this.state.payDownData.debts, 'amount') : 0;

        const outputDiv: JSX.Element = (
            <Output title="Your high interest debt totals:" main={FormatService.format(totalDebt)} />
        );

        const debtTable: JSX.Element = (
            <div className={`${styles.payDownDebtTable} drawerSection`}>
                <div className={styles.tableRow}>
                    <Typography variant={TypographyVariants.BODY}>Estimated payoff date:</Typography>
                    <Typography
                        variant={TypographyVariants.BODY_BOLD}
                        className={`${styles.capitalize} ${
                            snapshotDate?.isAfter(payoffDate) ? styles.improvement : ''
                        }`}
                    >
                        {payoffDateString || '—'}
                    </Typography>
                </div>
                <div className={styles.tableRow}>
                    <Typography variant={TypographyVariants.BODY}>Total interest costs over time:</Typography>
                    <Typography
                        variant={TypographyVariants.BODY_BOLD}
                        className={
                            this.state.payDownData &&
                            this.state.payDownData.totalInterestPaid < this.state.snapshot.totalInterestPaid
                                ? styles.improvement
                                : ''
                        }
                    >
                        {this.state.payDownData
                            ? FormatService.format(this.state.payDownData.totalInterestPaid)
                            : FormatService.format(0)}
                    </Typography>
                </div>
                <div className={styles.tableRow}>
                    <Typography variant={TypographyVariants.BODY}>Total estimated savings:</Typography>
                    <Typography
                        variant={TypographyVariants.BODY_BOLD}
                        className={
                            this.state.payDownData &&
                            this.state.payDownData.estimatedSavings > this.state.snapshot.estimatedSavings
                                ? styles.improvement
                                : ''
                        }
                        id="estimatedSavings"
                    >
                        {this.state.payDownData
                            ? FormatService.format(this.state.payDownData.estimatedSavings)
                            : FormatService.format(0)}
                    </Typography>
                </div>
                {infoCallout}
            </div>
        );

        const cost = Math.abs(this.state.monthly || 0);
        const slider: JSX.Element = (
            <BwSlider
                id="pay-down-debt-slider"
                label="How much extra do you want to pay each month?"
                value={cost}
                formattedValue={FormatService.formatCompactMoney(cost)}
                step={100}
                min={0}
                max={2000}
                onChange={async (value: any) => await this.handleMonthlyChanged(value)}
                inputToggleText="Add a precise amount"
            />
        );

        const debtsLabel: JSX.Element = <SectionHeader>Your high-interest debt</SectionHeader>;

        const debtsMarkup: JSX.Element[] = debts.map(
            (
                debt: {
                    amount: number;
                    id: number;
                    interest: number;
                    name: string;
                    payoffDate: string;
                },
                index: number
            ) => {
                const debtPayoffDate: LocalDate = LocalDate.parse(debt.payoffDate);
                const debtPayoffString: string = `${debtPayoffDate
                    .month()
                    .toString()
                    .toLowerCase()} ${debtPayoffDate.year()}`;
                return (
                    <div key={`debt${index}`} className={styles.debtCard}>
                        <div className={styles.cardColumn}>
                            <Typography variant={TypographyVariants.BODY_BOLD} className={styles.debtName}>
                                {debt.name}
                            </Typography>
                            <Typography variant={TypographyVariants.SUBTEXT}>
                                Interest rate: &nbsp;{Number(debt.interest * 100).toFixed(2) + '%'}
                            </Typography>
                            <Typography variant={TypographyVariants.SUBTEXT}>
                                Estimated payoff date: &nbsp;
                                <span className={styles.capitalize}>{debtPayoffString}</span>
                            </Typography>
                        </div>
                        <div className={`${styles.cardColumn} ${styles.rightColumn}`}>
                            <Typography variant={TypographyVariants.HEADING_S} className={styles.debtAmount}>
                                {FormatService.format(debt.amount)}
                            </Typography>
                            <Typography variant={TypographyVariants.CAPTION} className={styles.debtAmountCaption}>
                                Current balance
                            </Typography>
                        </div>
                    </div>
                );
            }
        );

        return (
            <GoalDrawer
                drawerProps={this.props.drawerProps}
                handleBack={this.props.handleBack}
                handleChangeCard={this.props.handleChangeCard}
                handleUpdateCard={this.props.handleUpdateCard}
                bottomButtonUpdate={this.handleUpdateOnce.bind(this)}
                handleDeleteCard={this.props.handleDeleteCard}
                showLoader={this.props.showLoader}
                hideLoader={this.props.hideLoader}
                icon={<PayDownDebtIcon className="icon" />}
                introContent={LiteralsService.getJsxLiteral('PayDownDebtDrawer.intro')}
                cashFlow={this.props.user.cashFlow}
                handleChangeCardDate={this.handleChangedStartDate.bind(this)}
                startDateLabel={creatingCard ? 'Plan to start' : 'Started'}
                learnMoreContent={LiteralsService.getJsxLiteral('PayDownDebtDrawer.introLink')}
                learnMoreUrl={HELP_DOMAIN ? `${HELP_DOMAIN}/articles/step-4-paying-down-expensive-debt` : null}
            >
                {outputDiv}
                {slider}
                {debtTable}
                {debts.length && (
                    <div className="drawerSection">
                        {debtsLabel}
                        <div>{debtsMarkup}</div>
                    </div>
                )}
            </GoalDrawer>
        );
    }

    private static async getData(date: string, monthly: number): Promise<PayDownData> {
        return await DebtService.getPayDownData(date, monthly);
    }

    private async handleChangedStartDate(value: LocalDate): Promise<void> {
        this.setState({
            startDate: value
        });

        // call debounced version of calc so it doesn't happen with every change
        await this.onChangeDebounced();
    }

    // debounced version of calcSavings() so it doesn't happen too often
    private readonly onChangeDebounced = _.debounce(this.calcSavings, 200);

    // change the monthly value so slider/input field can pick it up
    private async handleMonthlyChanged(value: number): Promise<void> {
        this.setState({
            monthly: value
        });
        // call debounced version of calc so it doesn't happen with every change
        await this.onChangeDebounced();
    }

    // calculate savings given the monthly amount
    private async calcSavings(): Promise<void> {
        const numValue = Number(String(this.state.monthly).replace(/[$,]/g, ''));
        const payDownData: PayDownData = await PayDownDebtDrawer.getData(this.state.startDate.toString(), numValue);
        if (!payDownData) return;
        this.setState({
            payDownData
        });
    }

    private async handleUpdate(): Promise<void> {
        const attributes: any = {
            ...this.props.drawerProps.card.attributes,
            monthly: this.state.monthly
        };
        const updatedCard: CardModel = {
            ...this.props.drawerProps.card,
            startDate: this.state.startDate,
            attributes
        };

        this.props.handleUpdateCard(updatedCard, this.props.user);
    }
}
